import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface SyncState {
  // Pending counts
  pendingWorkOrderUpdates: number
  pendingFiles: number
  pendingJsaForms: number
  totalPendingCount: number
  
  // Sync status
  isSyncing: boolean
  syncProgress: number // 0-100
  lastSyncTime: Date | null
  lastSyncError: string | null
  
  // Failed items tracking
  failedItems: {
    workOrderUpdates: number
    files: number
    jsaForms: number
  }
  
  // Actions
  updatePendingCounts: (counts: {
    workOrderUpdates: number
    files: number
    jsaForms: number
  }) => void
  
  setIsSyncing: (syncing: boolean) => void
  setSyncProgress: (progress: number) => void
  setLastSyncTime: (time: Date) => void
  setLastSyncError: (error: string | null) => void
  
  updateFailedCounts: (counts: {
    workOrderUpdates: number
    files: number
    jsaForms: number
  }) => void
  
  resetSyncState: () => void
}

export const useSyncStore = create<SyncState>()(
  persist(
    (set) => ({
      // Initial state
      pendingWorkOrderUpdates: 0,
      pendingFiles: 0,
      pendingJsaForms: 0,
      totalPendingCount: 0,
      
      isSyncing: false,
      syncProgress: 0,
      lastSyncTime: null,
      lastSyncError: null,
      
      failedItems: {
        workOrderUpdates: 0,
        files: 0,
        jsaForms: 0
      },
      
      // Actions
      updatePendingCounts: (counts) => set(() => ({
        pendingWorkOrderUpdates: counts.workOrderUpdates,
        pendingFiles: counts.files,
        pendingJsaForms: counts.jsaForms,
        totalPendingCount: counts.workOrderUpdates + counts.files + counts.jsaForms
      })),
      
      setIsSyncing: (syncing) => set({ 
        isSyncing: syncing,
        syncProgress: syncing ? 0 : 100 
      }),
      
      setSyncProgress: (progress) => set({ syncProgress: progress }),
      
      setLastSyncTime: (time) => set({ lastSyncTime: time }),
      
      setLastSyncError: (error) => set({ lastSyncError: error }),
      
      updateFailedCounts: (counts) => set({ 
        failedItems: counts 
      }),
      
      resetSyncState: () => set({
        isSyncing: false,
        syncProgress: 0,
        lastSyncError: null
      })
    }),
    {
      name: 'sync-storage',
      partialize: (state) => ({
        lastSyncTime: state.lastSyncTime,
        lastSyncError: state.lastSyncError
      })
    }
  )
)